import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy - DeckScore" },
      { name: "description", content: "How DeckScore collects, uses, and protects your information." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Privacy Policy - DeckScore" },
      { property: "og:description", content: "How DeckScore collects, uses, and protects your information." },
    ],
  }),
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy">
      <p><strong>Privacy Policy for DeckScore</strong></p>
      <p><strong>Effective Date: 11 July 2026</strong></p>
      <p>
        At DeckScore ("we," "our," or "us"), a service operated by Adviselo Collab Studios Private Limited, your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our platform and use our services, including our free AI pitch-deck analysis and our paid investor-introduction service.
      </p>

      <h2>1. Information We Collect</h2>
      <h3>a. Information You Provide to Us</h3>
      <ul>
        <li>Full name</li>
        <li>Email address</li>
        <li>Startup information (e.g. company name, sector, funding stage, pitch deck and pitch materials)</li>
        <li>One-pager and any documents you upload for analysis or investor outreach</li>
        <li>Communication preferences</li>
        <li>Any other data you enter when using the platform</li>
      </ul>
      <h3>b. Information Collected Automatically</h3>
      <ul>
        <li>IP address and location</li>
        <li>Browser type and device information</li>
        <li>Usage data and interaction logs</li>
        <li>Cookies and tracking technologies</li>
      </ul>
      <h3>c. Information from Third Parties</h3>
      <ul>
        <li>Public databases</li>
        <li>Enrichment services (e.g., when we verify investor or program data)</li>
        <li>Referral or partner platforms</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use your data to:</p>
      <ul>
        <li>Provide and improve our pitch-deck analysis, scoring, and investor-matching services.</li>
        <li>Prepare and send your one-pager to relevant investors when you purchase our paid service.</li>
        <li>Customize your user experience.</li>
        <li>Communicate updates, offers, and notifications.</li>
        <li>Ensure platform security and prevent fraud.</li>
      </ul>

      <h2>3. Sharing of Information</h2>
      <p>We do not sell your personal information. We may share data with:</p>
      <ul>
        <li>Investors and venture capital firms, but only the pitch/one-pager materials you explicitly submit for outreach as part of our paid service.</li>
        <li>Service providers who perform services on our behalf.</li>
        <li>Legal authorities when required by law.</li>
        <li>Business partners.</li>
      </ul>

      <h2>4. Cookies and Tracking</h2>
      <p>
        We use cookies to enhance your experience on our website. These small data files help us keep you signed in, remember your preferences, and track usage for analytics and performance.
      </p>
      <p><strong>Types of Cookies We Use:</strong></p>
      <ul>
        <li><strong>Necessary Cookies:</strong> Essential for basic functionalities such as page navigation, secure log-in, and filling out forms. The website cannot function properly without them.</li>
        <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our site by collecting and reporting information anonymously.</li>
        <li><strong>Marketing Cookies:</strong> Track users across websites to display personalized and relevant advertisements.</li>
      </ul>

      <h2>5. Data Retention</h2>
      <p>We retain your data only as long as necessary for the purposes mentioned in this policy or as required by law.</p>

      <h2>6. Your Rights under GDPR</h2>
      <p>
        If you are a resident of the European Economic Area (EEA), you are entitled to certain rights under the GDPR, including: Right to Access, Right to Rectification, Right to Erasure, Right to Restrict Processing, Right to Data Portability, Right to Object, Right to Avoid Automated Decision-Making, Right to Withdraw Consent, and Right to Lodge a Complaint. To exercise any of these rights, contact us at <a href="mailto:info@ikomatch.com">info@ikomatch.com</a>. We will respond within 30 days and may ask you to verify your identity first.
      </p>

      <h2>7. Data Security</h2>
      <p>We implement reasonable safeguards including encryption and access controls to protect your data.</p>

      <h2>8. Children's Privacy</h2>
      <p>
        Our services are not directed to individuals under the age of 18. We do not knowingly collect personal data from minors and will delete any such data promptly if discovered.
      </p>

      <h2>9. International Data Transfers</h2>
      <p>
        If you access our services from outside India, your data may be transferred to, stored, and processed in India or other jurisdictions where our servers or third-party service providers are located. By using our services, you consent to such transfers and processing.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we make material updates, we will notify you via email or a prominent notice on our website prior to the changes becoming effective.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        Email: <a href="mailto:info@ikomatch.com">info@ikomatch.com</a> · Adviselo Collab Studios Private Limited, Surat, Gujarat, India.
      </p>
    </LegalPage>
  );
}
